import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useSortedPagination from "../../hooks/useSortedPagination";
import Pill from "../Generic/Pill";
import { getDefaultMonthToDateRange } from "../../helper/dateFormat";
import AppDateRangePicker from "../Generic/AppDateRangePicker";
import { lightThemeColors } from "../../assets/styles/colors";
import { useProjectContext } from "../../context/useProjectContext";
import PrimaryButton from "../Generic/PrimaryButton";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import CustomModal from "../Generic/CustomModal";
import { showGenericError, showErrorNotification } from "../../helper/Notifications";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import CustomInput from "../Generic/CustomInput";
import { useLicensingContext } from "../../context/useLicensingContext";
import BulkActionBar from "../Generic/BulkActionBar";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../Generic/StyledTable";
import TablePagination from "../Generic/TablePagination";
import Dropdown from "../Generic/Dropdown";
import SubscriptionCards from "../../Pages/SubscriptionsPage/SubscriptionCards";
import Toggle from "../Generic/Toggle";
import { CURRENCY_OPTIONS } from "../../constants/OptionsConstants";
import LoaderComponent from "../Generic/LoaderComponent";
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import PageHeader from "../Layout/PageHeader";
import ProductUsageWidget from "../Generic/ProductUsageWidget";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import MemberAccessBanner from "../Generic/MemberAccessBanner";
import { formatNumber } from "../../helper/numberFormat";

const STATUS_VARIANT = { active: "success", invited: "warning", inactive: "default" };

function getStatusInfo(user) {
  if (user.disabled_user) return { key: "inactive", label: "Inactive" };
  if (user.confirmed) return { key: "active", label: "Active" };
  return { key: "invited", label: "Invite sent" };
}

function apiStatusFilter(uiStatus) {
  if (uiStatus === "invited") return "pending";
  if (uiStatus === "active") return "confirmed";
  return "";
}

const SeatManagementPage = ({
  productSlug,
  title,
  noAccessTitle,
  subtitle,
  noAccessSubtitle,
  deleteModalBody,
  bulkDeletePlatformName,
  emptyStateText,
  extraColumns = [],
  headerExtra = null,
  quickStartSection = null,
  showRestrictedCard = false,
  statusFilterPosition = "end",
}) => {
  const {
    getProjectUsers,
    selectedProject,
    disableProjectUsers,
    projectUsers,
    addProjectUsers,
  } = useProjectContext();
  const { setLoading, setShowSalesModal } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasProductAccess: hasMemberAccess } = useMemberProductAccess(productSlug);

  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();

  const {
    subscription,
    numberOfConfirmedUsers,
    canAddUsers,
    getProductUsageBreakdown,
  } = useLicensingContext();

  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userToDelete, setUserToDelete] = useState(null);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(true);

  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const {
    page, setPage, totalPages, setTotalPages,
    sortBy, ascending, toggleSort, sortArrow, goToPrevious, goToNext,
  } = useSortedPagination({ initialSortBy: "words", initialAscending: false });

  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);
  const [usageByEmail, setUsageByEmail] = useState({});
  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");

  const toAPIDateRange = ([start, end]) => {
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(23, 59, 59, 999);
    return { start_date: s.toISOString(), end_date: e.toISOString() };
  };

  const loadProjectUsers = (projectId, term, pg, status) => {
    setLoading(true);
    const { start_date, end_date } = toAPIDateRange(dateRange);
    getProjectUsers(
      projectId, term, pg, itemsPerPage, apiStatusFilter(status), productSlug,
      (response) => { setTotalPages(response.data.pagination?.total_pages || 1); setLoading(false); },
      () => { showGenericError(); setLoading(false); },
      { sortBy, ascending, startDate: start_date, endDate: end_date }
    );
  };

  const loadUsageByEmail = (range) => {
    if (!selectedProject || !range) return;
    const payload = {
      ...toAPIDateRange(range),
      page: 1, items_per_page: 1000,
      sort_by: "words", ascending: false, term: "",
    };
    getProductUsageBreakdown(
      selectedProject.id, productSlug, payload,
      (res) => {
        const items = res.data?.members || res.data?.items || [];
        const map = {};
        items.forEach((item) => {
          const email = item.user?.email || item.seat?.email;
          if (email) {
            map[email] = {
              words: item.words ?? 0,
              audio: item.audio ?? item.audio_minutes ?? item.audio_min ?? 0,
              video: item.video ?? item.video_minutes ?? item.video_min ?? 0,
            };
          }
        });
        setUsageByEmail(map);
      },
      (err) => console.error("Usage-by-email error:", err)
    );
  };

  const handleDeleteUser = () => {
    if (!userToDelete || !selectedProject) return Promise.reject(new Error("Missing required data"));
    return new Promise((resolve, reject) => {
      disableProjectUsers(
        selectedProject.id, [userToDelete.email], productSlug,
        (res) => { loadProjectUsers(selectedProject.id, searchTerm, page, statusFilter); resolve(res); },
        (err) => { showGenericError(); reject(err); }
      );
    });
  };

  const handleBulkDeleteUsers = () =>
    new Promise((resolve, reject) => {
      setLoading(true);
      disableProjectUsers(
        selectedProject.id, selectedUsers, productSlug,
        (res) => {
          loadProjectUsers(selectedProject.id, searchTerm, page, statusFilter);
          setSelectedUsers([]);
          setLoading(false);
          resolve(res);
        },
        (err) => { showGenericError(); setLoading(false); reject(err); }
      );
    });

  const handleAddSeats = () => {
    if (!emailInputValue || !selectedProject) return Promise.reject(new Error("Missing email or project"));
    const emailArray = Array.from(new Set(
      emailInputValue.replace(/\n/g, ",").replace(/\s+/g, "").split(",")
        .map((e) => e.toLowerCase()).filter(Boolean)
    ));
    return new Promise((resolve, reject) => {
      canAddUsers(
        emailArray, selectedProject.id,
        () => {
          addProjectUsers(
            selectedProject.id, emailArray, productSlug,
            (res) => { loadProjectUsers(selectedProject.id, searchTerm, page, statusFilter); setEmailInputValue(""); resolve(res); },
            (err) => {
              const users = err?.response?.data?.users || [];
              const list = users.length ? ` ${users.join(", ")}` : "";
              if (err?.response?.data?.message === "Can not add users, they are already enabled in this or other projects") {
                showErrorNotification(`Can not add users, they are already enabled in this or other subscriptions.${list}`);
              } else {
                showGenericError();
              }
              reject(err);
            }
          );
        },
        (err) => {
          const users = err?.data?.users || err?.response?.data?.users || [];
          const list = users.length ? ` ${users.join(", ")}` : "";
          showErrorNotification(`Can not add users, they are already enabled in this or other subscriptions.${list}`);
          reject(err);
        }
      );
    });
  };

  useEffect(() => {
    if (selectedProject) { setPage(1); setSearchTerm(""); setStatusFilter("active"); }
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!selectedProject) return;
    if (!subscription && !hasMemberAccess) { setIsPageLoading(false); return; }
    loadProjectUsers(selectedProject.id, searchTerm, page, statusFilter);
  }, [selectedProject, page, itemsPerPage, searchTerm, statusFilter, subscription, hasMemberAccess, sortBy, ascending, dateRange]);

  useEffect(() => {
    if (subscription === undefined) return;
    if (!subscription && !hasMemberAccess) { setIsPageLoading(false); return; }
    if (!selectedProject) return;
    numberOfConfirmedUsers(
      selectedProject.id,
      () => setIsPageLoading(false),
      () => { setIsPageLoading(false); showGenericError(); }
    );
  }, [subscription, hasMemberAccess]);

  useEffect(() => {
    if (!selectedProject) return;
    if (!subscription && !hasMemberAccess) return;
    loadUsageByEmail(dateRange);
  }, [selectedProject, dateRange, hasMemberAccess, subscription]);

  useEffect(() => {
    if (subscription?.currency) setCurrency(subscription.currency);
  }, [subscription]);

  useEffect(() => {
    if (location?.state?.openAddSeats && !showRestrictedCard) {
      setShowAddUsersModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state, showRestrictedCard]);

  const hasProduct = subscription?.items?.some((i) => i.product_slug === productSlug) || hasMemberAccess;
  const hasSelection = selectedUsers.length > 0;
  const totalCols = 6 + extraColumns.length;

  const displayedUsers = (() => {
    let users = projectUsers || [];
    if (statusFilter === "inactive") users = users.filter((u) => u.disabled_user);
    else if (statusFilter === "active") users = users.filter((u) => u.confirmed && !u.disabled_user);
    return users;
  })();

  const renderCtx = { openAddSeats: () => setShowAddUsersModal(true), isPageLoading };

  if (subscription === undefined) return <LoaderComponent loading={true} />;

  const statusDropdown = (
    <div style={{ minWidth: 160 }}>
      <Dropdown
        value={statusFilter}
        onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
        placeholder="All statuses"
        className="h-9 py-1.5"
        options={[
          { value: "all", label: "All statuses" },
          { value: "active", label: "Active" },
          { value: "invited", label: "Invite sent" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
    </div>
  );

  return (
    <Container>
      <CustomModal
        isOpen={showAddUsersModal}
        onOpenChange={() => { setShowAddUsersModal(false); setEmailInputValue(""); }}
        iconType="userPlus"
        iconAlignment="left"
        title="Assign seats"
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          { text: "Cancel", onClick: () => { setShowAddUsersModal(false); setEmailInputValue(""); }, variant: "secondary" },
          { text: "Send Invites", action: handleAddSeats, variant: "primary", disabled: !emailInputValue.trim(), successMessage: "Invites sent successfully" },
        ]}
      >
        <ModalContent>
          <ModalDescription>
            Enter one or more email addresses, separated by commas. Each email will receive an invitation. Once accepted, the seat will be assigned and activated.
          </ModalDescription>
          <BillingNotice>Billing only starts after a seat is confirmed.</BillingNotice>
          <EmailFieldLabel>Email address</EmailFieldLabel>
          <EmailTextArea
            placeholder="Eq. john@emotii.com, maria@emotii.com"
            value={emailInputValue}
            onChange={(e) => setEmailInputValue(e.target.value)}
            rows={4}
          />
        </ModalContent>
      </CustomModal>

      <CustomModal
        isOpen={showDeleteModal}
        onOpenChange={() => { setShowDeleteModal(false); setUserToDelete(null); }}
        iconType="warning"
        iconAlignment="center"
        title="Remove this seat?"
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          { text: "Cancel", onClick: () => { setShowDeleteModal(false); setUserToDelete(null); }, variant: "secondary" },
          { text: "Delete", action: handleDeleteUser, variant: "delete", successMessage: "Seat removed successfully" },
        ]}
      >
        <p style={{ textAlign: "center", marginBottom: "16px" }}>{deleteModalBody}</p>
      </CustomModal>

      <CustomModal
        isOpen={showBulkDeleteModal}
        onOpenChange={() => setShowBulkDeleteModal(false)}
        iconType="warning"
        iconAlignment="center"
        title={`Remove ${selectedUsers.length} seat${selectedUsers.length === 1 ? "" : "s"}?`}
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          { text: "Cancel", onClick: () => setShowBulkDeleteModal(false), variant: "secondary" },
          {
            text: `Remove ${selectedUsers.length} Seat${selectedUsers.length === 1 ? "" : "s"}`,
            action: handleBulkDeleteUsers,
            variant: "delete",
            successMessage: `Seat${selectedUsers.length === 1 ? "" : "s"} removed successfully`,
          },
        ]}
      >
        <p style={{ textAlign: "center", marginBottom: "16px" }}>
          This will immediately remove <strong>{selectedUsers.length}</strong> user{selectedUsers.length === 1 ? "" : "s"}' access to {bulkDeletePlatformName} and permanently delete their associated data.
        </p>
      </CustomModal>

      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={hasProduct ? title : noAccessTitle}
              subtitle={hasProduct ? subtitle : noAccessSubtitle}
            />
          </HeaderContent>
          <HeaderRight>
            {!hasProduct && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
            {hasProduct && (
              <div className="flex items-center gap-3">
                {headerExtra && headerExtra(renderCtx)}
                {!showRestrictedCard && (
                  <PrimaryButton
                    icon={<AddSeatsIcon />}
                    text="Add Seats"
                    onClick={() => setShowAddUsersModal(true)}
                    disabled={subscription?.status === "canceled"}
                  />
                )}
              </div>
            )}
          </HeaderRight>
        </Header>

        {quickStartSection && quickStartSection(renderCtx)}

        {hasProduct && <MemberAccessBanner seatBased />}

        {!hasProduct && (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle checked={annualPricing} onCheckedChange={setAnnualPricing} />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>
            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSalesModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product={productSlug}
            />
          </PlanCardsSection>
        )}

        {hasProduct && (
          <>
            {showRestrictedCard ? (
              <RestrictedAccessCard>
                <RestrictedContent>
                  <RestrictedTitle>Do more with emotii.ai</RestrictedTitle>
                  <RestrictedDescription>
                    With emotii.ai, connecting across languages and cultures feels effortless. Don't just translate—communicate. Instantly. Intelligently. At scale.
                  </RestrictedDescription>
                  <RestrictedDescription>
                    Your current plan doesn't include emotii.ai applications. Check out{" "}
                    <a href="https://emotii.ai/pricing/" target="_blank" rel="noopener noreferrer">pricing</a>{" "}
                    to find out what is included in the Business Pro plan or talk with our team for a custom offer.
                  </RestrictedDescription>
                  <ButtonWrapper>
                    <PrimaryButton
                      text="Talk with our Team"
                      onClick={() => {
                        if (typeof setShowSalesModal === "function") setShowSalesModal(true);
                        else navigate("/subscriptions");
                      }}
                      styled={{ width: "100%", padding: "16px", fontSize: "16px", borderRadius: "10px" }}
                    />
                  </ButtonWrapper>
                </RestrictedContent>
              </RestrictedAccessCard>
            ) : (
              <>
                {selectedProject && (
                  <ProductUsageWidget
                    projectId={selectedProject.id}
                    productSlug={productSlug}
                    dateRange={dateRange}
                    calendarAction={
                      <AppDateRangePicker value={dateRange} onChange={setDateRange} />
                    }
                  />
                )}

                <TableControls>
                  <FiltersGroup>
                    <CustomInput
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.currentTarget.value); setPage(1); }}
                      placeholder="Search users..."
                      wrapperStyle={{ maxWidth: "400px" }}
                      className="h-9 py-1.5"
                    />
                    {statusFilterPosition === "start" && statusDropdown}
                  </FiltersGroup>

                  {statusFilterPosition === "end" && statusDropdown}
                </TableControls>

                <StyledTable>
                  <TableHeader>
                    <BulkActionBar
                      selectedCount={selectedUsers.length}
                      onDelete={() => setShowBulkDeleteModal(true)}
                      onClear={() => setSelectedUsers([])}
                    />
                    <TableRow>
                      <TableHead style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                        <CheckboxContainer $alwaysVisible={hasSelection}>
                          <input
                            type="checkbox"
                            style={{ cursor: "pointer", margin: 0 }}
                            checked={displayedUsers?.length > 0 && selectedUsers.length === displayedUsers.length}
                            onChange={(e) => e.target.checked
                              ? setSelectedUsers(displayedUsers.map((u) => u.email))
                              : setSelectedUsers([])}
                          />
                        </CheckboxContainer>
                      </TableHead>
                      <TableHead style={{ textAlign: "left", width: "30%" }}>User</TableHead>
                      <TableHead style={{ textAlign: "center" }}>Status</TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => toggleSort("words")} active={sortBy === "words"}>
                          Words{sortArrow("words")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => toggleSort("audio")} active={sortBy === "audio"}>
                          Audio min{sortArrow("audio")}
                        </SortButton>
                      </TableHead>
                      {extraColumns.map((col) => (
                        <TableHead key={col.key} style={{ textAlign: "center" }}>
                          <SortButton onClick={() => toggleSort(col.key)} active={sortBy === col.key}>
                            {col.label}{sortArrow(col.key)}
                          </SortButton>
                        </TableHead>
                      ))}
                      <TableHead style={{ width: 50, textAlign: "center" }}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedUsers.length > 0 ? (
                      displayedUsers.map((u) => {
                        const usage = usageByEmail[u.email] || { words: 0, audio: 0, video: 0 };
                        const { key: statusKey, label: statusLabel } = getStatusInfo(u);
                        return (
                          <TableRow key={u.id}>
                            <TableCell style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                              <CheckboxContainer $alwaysVisible={hasSelection || selectedUsers.includes(u.email)}>
                                <input
                                  type="checkbox"
                                  style={{ cursor: "pointer", margin: 0 }}
                                  checked={selectedUsers.includes(u.email)}
                                  onChange={(e) => e.target.checked
                                    ? setSelectedUsers((prev) => [...prev, u.email])
                                    : setSelectedUsers((prev) => prev.filter((em) => em !== u.email))
                                  }
                                />
                              </CheckboxContainer>
                            </TableCell>
                            <TableCell>
                              <UserCell>
                                <UserSvgIcon />
                                <UserEmail>{u.email}</UserEmail>
                              </UserCell>
                            </TableCell>
                            <TableCell style={{ textAlign: "center" }}>
                              <Pill variant={STATUS_VARIANT[statusKey]} size="sm" title={`Since: ${moment(u.created_at).format("DD/MM/YYYY")}`}>
                                {statusLabel}
                              </Pill>
                            </TableCell>
                            <TableCell style={{ textAlign: "center" }}>{formatNumber(usage.words)}</TableCell>
                            <TableCell style={{ textAlign: "center" }}>{formatNumber(usage.audio)}</TableCell>
                            {extraColumns.map((col) => (
                              <TableCell key={col.key} style={{ textAlign: "center" }}>
                                {formatNumber(col.getFromItem ? col.getFromItem(usage, u) : (usage[col.key] ?? 0))}
                              </TableCell>
                            ))}
                            <TableCell style={{ textAlign: "center" }}>
                              <DeleteButton onClick={() => { setUserToDelete(u); setShowDeleteModal(true); }}>
                                <BinIcon />
                              </DeleteButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={totalCols}>
                          <EmptyState>{emptyStateText}</EmptyState>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TablePagination
                    page={page}
                    totalPages={totalPages}
                    onPrevious={goToPrevious}
                    onNext={goToNext}
                    leftColSpan={2}
                    centerColSpan={2 + extraColumns.length}
                    rightColSpan={2}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
                  />
                </StyledTable>
              </>
            )}
          </>
        )}
      </PageContent>
    </Container>
  );
};

export default SeatManagementPage;

const AddSeatsIcon = (props) => (
  <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M14.1666 16.5V14.8333C14.1666 13.9493 13.8155 13.1014 13.1903 12.4763C12.5652 11.8512 11.7174 11.5 10.8333 11.5H4.16665C3.28259 11.5 2.43474 11.8512 1.80962 12.4763C1.1845 13.1014 0.833313 13.9493 0.833313 14.8333V16.5M19.1666 16.5V14.8333C19.1661 14.0948 18.9203 13.3773 18.4678 12.7936C18.0153 12.2099 17.3818 11.793 16.6666 11.6083M13.3333 1.60833C14.0503 1.79192 14.6858 2.20892 15.1397 2.79359C15.5935 3.37827 15.8398 4.09736 15.8398 4.8375C15.8398 5.57764 15.5935 6.29673 15.1397 6.88141C14.6858 7.46608 14.0503 7.88308 13.3333 8.06667M10.8333 4.83333C10.8333 6.67428 9.34093 8.16667 7.49998 8.16667C5.65903 8.16667 4.16665 6.67428 4.16665 4.83333C4.16665 2.99238 5.65903 1.5 7.49998 1.5C9.34093 1.5 10.8333 2.99238 10.8333 4.83333Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserSvgIcon = () => (
  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect width="40" height="40" rx="20" fill="#D0E9FF"/>
    <path d="M25 29V27C25 25.9391 24.5786 24.9217 23.8284 24.1716C23.0783 23.4214 22.0609 23 21 23H13C11.9391 23 10.9217 23.4214 10.1716 24.1716C9.42143 24.9217 9 25.9391 9 27V29M31 29V27C30.9993 26.1137 30.7044 25.2528 30.1614 24.5523C29.6184 23.8519 28.8581 23.3516 28 23.13M24 11.13C24.8604 11.3503 25.623 11.8507 26.1676 12.5523C26.7122 13.2539 27.0078 14.1168 27.0078 15.005C27.0078 15.8932 26.7122 16.7561 26.1676 17.4577C25.623 18.1593 24.8604 18.6597 24 18.88M21 15C21 17.2091 19.2091 19 17 19C14.7909 19 13 17.2091 13 15C13 12.7909 14.7909 11 17 11C19.2091 11 21 12.7909 21 15Z" stroke="#1470EF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px;
  height: 100%;
  background: #F9FAFB;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const TableControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const FiltersGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
`;

const CheckboxContainer = styled.div`
  opacity: ${(p) => (p.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  tr:hover & { opacity: 1; }
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserEmail = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${(p) => (p.active ? "700" : "600")};
  color: ${(p) => (p.active ? "#111827" : "#374151")};
  padding: 0;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;
  &:hover { background: #fef2f2; color: #ef4444; }
  svg { width: 16px; height: 16px; fill: none !important; stroke: currentColor !important; stroke-width: 1.5 !important; }
  svg path { fill: none !important; stroke: currentColor !important; stroke-width: 1.5 !important; }
`;

const EmptyState = styled.div`
  color: ${lightThemeColors.darkColor50};
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  padding: 40px 20px;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
`;

const ModalDescription = styled.p`
  color: ${lightThemeColors.darkColor50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const BillingNotice = styled.p`
  color: ${lightThemeColors.darkColor50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const EmailFieldLabel = styled.label`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  display: block;
  margin-bottom: 6px;
`;

const EmailTextArea = styled.textarea`
  background: none;
  min-height: 120px;
  padding: 12px 16px;
  outline: none;
  width: 100%;
  color: ${lightThemeColors.darkColor};
  border-radius: 8px;
  border: 1px solid ${lightThemeColors.sidebar.menuItemBorder};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  &::placeholder { color: ${lightThemeColors.darkColor50}; }
  &:focus {
    border-color: ${lightThemeColors.primaryColor};
    box-shadow: 0 0 0 3px rgba(112, 105, 253, 0.1);
  }
`;

const RestrictedAccessCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 48px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const RestrictedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RestrictedTitle = styled.h2`
  color: #7f56d9;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

const RestrictedDescription = styled.p`
  color: ${lightThemeColors.darkColor};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
  button { width: 100%; }
`;
